import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { BASE_URL } from '../../components/Constant';

// ─── Shared lightbox styles ───────────────────────────────────────────────────
const lightboxStyle = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    cursor: 'zoom-out'
  },
  img: {
    width: '100%',
    height: '100%',
    borderRadius: '6px',
    boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
    objectFit: 'contain',
    cursor: 'default'
  },
  close: {
    position: 'absolute',
    top: '16px',
    right: '24px',
    fontSize: '32px',
    color: '#fff',
    cursor: 'pointer',
    lineHeight: 1,
    userSelect: 'none'
  },
  arrow: {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: '40px',
    color: '#fff',
    cursor: 'pointer',
    userSelect: 'none',
    padding: '0 16px',
    opacity: 0.8
  },
  counter: {
    position: 'absolute',
    bottom: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    color: '#fff',
    fontSize: '13px',
    background: 'rgba(0,0,0,0.4)',
    padding: '2px 10px',
    borderRadius: '12px'
  },
  downloadBtn: {
    position: 'absolute',
    top: '16px',
    left: '24px',
    color: '#fff',
    fontSize: '13px',
    background: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.4)',
    borderRadius: '6px',
    padding: '4px 12px',
    cursor: 'pointer',
    textDecoration: 'none'
  }
};

/**
 * ImageFullScreenDialog
 *
 * Handles both legacy (single string) and new (array of strings) image values.
 * Renders thumbnails in the table cell. Clicking a thumbnail opens an inline
 * lightbox overlay — no new page or tab is opened.
 *
 * Supports:
 *  - images: [String]  (new multi-upload records)
 *  - value:  String    (legacy single-image records)
 *  - value:  File      (file object, e.g. during upload preview)
 */
export default function ImageFullScreenDialog({ value }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const isOpen = lightboxIndex !== null;

  // ── Normalise value → array of { url, isPdf } ──────────────────────────────
  const buildUrls = () => {
    // New: images array passed directly
    if (Array.isArray(value)) {
      return value.map((v) => {
        // eslint-disable-next-line no-undef
        const isFile = typeof window !== 'undefined' && v instanceof File;
        // eslint-disable-next-line no-undef
        const url = isFile ? URL.createObjectURL(v) : v;
        const isPdf = isFile ? v.type === 'application/pdf' : typeof v === 'string' && v.toLowerCase().includes('.pdf');
        return { url, isPdf };
      });
    }

    // Legacy: single string or File
    // eslint-disable-next-line no-undef
    const isFile = typeof window !== 'undefined' && value instanceof File;
    if (!value && !isFile) return [];
    // eslint-disable-next-line no-undef
    const url = isFile ? URL.createObjectURL(value) : value;
    const isPdf = isFile
      ? value.type === 'application/pdf'
      : typeof value === 'string' && value.toLowerCase().includes('.pdf');
    return [{ url, isPdf }];
  };

  const urls = buildUrls();

  // ── Keyboard navigation ───────────────────────────────────────────────────
  React.useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxIndex(null);
      if (e.key === 'ArrowRight') setLightboxIndex((i) => (i + 1) % urls.length);
      if (e.key === 'ArrowLeft') setLightboxIndex((i) => (i - 1 + urls.length) % urls.length);
    };
    // eslint-disable-next-line no-undef
    window.addEventListener('keydown', onKey);
    // eslint-disable-next-line no-undef
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, urls.length]);

  if (!urls.length) {
    return <span style={{ color: '#aaa', fontSize: '12px' }}>—</span>;
  }

  const current = urls[lightboxIndex] || {};

  const handleDownload = async (e, url) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (url.startsWith('blob:')) {
        // eslint-disable-next-line no-undef
        const link = document.createElement('a');
        link.href = url;
        link.download = 'attachment';
        // eslint-disable-next-line no-undef
        document.body.appendChild(link);
        link.click();
        // eslint-disable-next-line no-undef
        document.body.removeChild(link);
        return;
      }

      // Call our authenticated backend proxy to bypass client-side CORS issues!
      const downloadProxyUrl = `${BASE_URL}upload/download?url=${encodeURIComponent(url)}`;
      // eslint-disable-next-line no-undef
      const response = await fetch(downloadProxyUrl, {
        headers: {
          // eslint-disable-next-line no-undef
          authorization: `${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Proxy download failed');
      }

      const blob = await response.blob();
      // eslint-disable-next-line no-undef
      const blobUrl = URL.createObjectURL(blob);

      // eslint-disable-next-line no-undef
      const link = document.createElement('a');
      link.href = blobUrl;
      const filename = url.split('/').pop().split('?')[0] || 'download';
      link.download = filename;

      // eslint-disable-next-line no-undef
      document.body.appendChild(link);
      link.click();
      // eslint-disable-next-line no-undef
      document.body.removeChild(link);
      // eslint-disable-next-line no-undef
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // Fallback if proxy fetch fails: open in a new window/tab as a last resort
      // eslint-disable-next-line no-undef
      window.open(url, '_blank');
    }
  };

  return (
    <>
      {/* ── Thumbnails in table cell ─────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
        {urls.map(({ url, isPdf }, i) =>
          isPdf ? (
            // PDF thumbnail — show icon, click to open lightbox
            <div
              key={i}
              onClick={() => setLightboxIndex(i)}
              title="Click to preview PDF"
              style={{
                width: '30px',
                height: '30px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'zoom-in',
                background: '#fef0f0',
                borderRadius: '3px',
                border: '1px solid #f5c6c6'
              }}
            >
              {/* Simple PDF icon using text */}
              <span style={{ fontSize: '18px' }}>📄</span>
            </div>
          ) : (
            <img
              key={i}
              src={url}
              alt={`attachment-${i + 1}`}
              onClick={() => setLightboxIndex(i)}
              style={{
                width: '30px',
                height: '30px',
                objectFit: 'cover',
                borderRadius: '3px',
                cursor: 'zoom-in',
                border: '1px solid #ddd'
              }}
            />
          )
        )}
      </div>

      {/* ── Lightbox overlay ─────────────────────────────────────────────── */}
      {isOpen && (
        <div style={lightboxStyle.overlay} onClick={() => setLightboxIndex(null)}>
          {/* Download link */}
          <a href={current.url} style={lightboxStyle.downloadBtn} onClick={(e) => handleDownload(e, current.url)}>
            ↓ Download
          </a>

          {/* Close */}
          <span style={lightboxStyle.close} onClick={() => setLightboxIndex(null)}>
            ✕
          </span>

          {/* Prev arrow */}
          {urls.length > 1 && (
            <span
              style={{ ...lightboxStyle.arrow, left: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((lightboxIndex - 1 + urls.length) % urls.length);
              }}
            >
              ‹
            </span>
          )}

          {/* Main content */}
          {current.isPdf ? (
            <iframe
              src={current.url}
              title={`pdf-${lightboxIndex}`}
              style={{
                width: '90vw',
                height: '88vh',
                border: 'none',
                borderRadius: '6px'
              }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <img
              src={current.url}
              alt={`attachment-${lightboxIndex + 1}`}
              style={lightboxStyle.img}
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {/* Next arrow */}
          {urls.length > 1 && (
            <span
              style={{ ...lightboxStyle.arrow, right: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                setLightboxIndex((lightboxIndex + 1) % urls.length);
              }}
            >
              ›
            </span>
          )}

          {/* Counter */}
          {urls.length > 1 && (
            <span style={lightboxStyle.counter}>
              {lightboxIndex + 1} / {urls.length}
            </span>
          )}
        </div>
      )}
    </>
  );
}

ImageFullScreenDialog.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    // eslint-disable-next-line no-undef
    PropTypes.instanceOf(typeof File !== 'undefined' ? File : Object)
  ])
};
